import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Document, Page, pdfjs } from "react-pdf";
import { ArrowBackOutlined, ArrowForwardOutlined } from "@mui/icons-material";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url
).toString();
const validationSchema = Yup.object({
  nama_sop: Yup.string().required("Nama SOP wajib diisi"),
  file_sop: Yup.mixed()
    .required("File SOP wajib dipilih")
    .test("fileType", "Hanya mendukung PDF atau gambar", (value) => {
      return (
        value &&
        ["application/pdf", "image/jpeg", "image/png"].includes(value.type)
      );
    }),
});

const AddPanduanModal = ({ open, onClose, onSubmit }) => {
  const theme = useTheme();
  const [filePreview, setFilePreview] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const formik = useFormik({
    initialValues: {
      nama_sop: "",
      file_sop: null,
    },
    validationSchema,
    onSubmit: (values) => {
      const reqData = {
        nama_sop: values.nama_sop,
        file_sop: values.file_sop,
      };
      onSubmit(reqData);
      formik.resetForm();
      setFilePreview(null);
      onClose();
    },
  });

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      formik.setFieldValue("file_sop", file);
      setFilePreview(file);
      setNumPages(null); // Reset number of pages on new file
      setPageNumber(1); // Reset page number on new file
    }
  };
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => {
    setPageNumber((prevPageNumber) => Math.max(1, prevPageNumber - 1));
  };

  const goToNextPage = () => {
    setPageNumber((prevPageNumber) =>
      Math.min(numPages || 1, prevPageNumber + 1)
    );
  };

  const renderFilePreview = () => {
    if (!filePreview) return null;

    const fileType = filePreview.type;
    if (fileType === "application/pdf") {
      return (
        <div>
          <Document
            file={filePreview}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => console.error("PDF load error", error)}
          >
            <Page pageNumber={pageNumber} />
          </Document>
          {numPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 10,
              }}
            >
              <Button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                size="small"
                color="warning"
              >
                <ArrowBackOutlined />
              </Button>

              <Typography style={{ margin: "0 10px", lineHeight: "2" }}>
                {pageNumber} / {numPages}
              </Typography>

              <Button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                size="small"
                color="warning"
              >
                <ArrowForwardOutlined />
              </Button>
            </div>
          )}
        </div>
      );
    } else if (fileType.startsWith("image/")) {
      return (
        <img
          src={URL.createObjectURL(filePreview)}
          alt="Preview"
          style={{ maxWidth: "100%", maxHeight: "300px", marginTop: 10 }}
        />
      );
    } else {
      return (
        <Typography variant="body2">
          Preview tidak tersedia untuk tipe ini.
        </Typography>
      );
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Tambah Panduan SOP</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nama SOP"
                name="nama_sop"
                value={formik.values.nama_sop}
                onChange={formik.handleChange}
                error={
                  formik.touched.nama_sop && Boolean(formik.errors.nama_sop)
                }
                helperText={formik.touched.nama_sop && formik.errors.nama_sop}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" component="label" fullWidth>
                Pilih File Panduan/SOP (PDF)
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {formik.touched.file_sop && formik.errors.file_sop && (
                <Typography color="error" variant="body2" mt={1}>
                  {formik.errors.file_sop}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              {renderFilePreview()}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="inherit"
            onClick={() => {
              onClose();
              setFilePreview(null);
              formik.resetForm();
            }}
            sx={{ fontSize: "14px", fontWeight: "bold", padding: "10px 20px" }}
          >
            Batal
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.background.alt,
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: theme.palette.secondary[500],
              },
            }}
          >
            Simpan
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddPanduanModal;
